import { useEffect, useState } from "react";
import { applyVerification, fetchMyVerification, uploadFile, fileUrl } from "@/lib/api";
import { BadgeCheck, ShieldCheck, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function VerificationCard() {
  const [v, setV] = useState(null);
  const [open, setOpen] = useState(false);
  const [idType, setIdType] = useState("Passport");
  const [idNumber, setIdNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [docPath, setDocPath] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => fetchMyVerification().then(setV).catch(() => setV({ status: "none" }));
  useEffect(() => { load(); }, []);

  if (!v) return null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Upload an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8MB"); return; }
    setUploading(true);
    try {
      const r = await uploadFile(file);
      setDocPath(r.path);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!idNumber.trim()) { toast.error("ID number required"); return; }
    if (!docPath) { toast.error("Please upload your ID document image"); return; }
    try {
      await applyVerification({ id_type: idType, id_number: idNumber.trim(), notes, doc_path: docPath });
      toast.success("Application submitted");
      setOpen(false);
      setIdNumber("");
      setNotes("");
      setDocPath("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const badge =
    v.status === "approved" ? { text: "VERIFIED", tone: "bg-primary text-primary-foreground" } :
    v.status === "pending" ? { text: "UNDER REVIEW", tone: "border border-foreground/40" } :
    v.status === "rejected" ? { text: "REJECTED", tone: "border border-destructive text-destructive" } :
    null;

  return (
    <div data-testid="verification-card" className="border border-foreground/15 bg-card p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          {v.status === "approved" ? (
            <BadgeCheck className="w-8 h-8 fill-primary text-primary-foreground" strokeWidth={2}/>
          ) : (
            <ShieldCheck className="w-8 h-8 text-muted-foreground" strokeWidth={1.5}/>
          )}
          <div>
            <div className="overline mb-1">◆ Seller verification</div>
            <div className="font-semibold">
              {v.status === "approved" ? "You're verified" :
               v.status === "pending" ? "Waiting on admin review" :
               v.status === "rejected" ? "Application rejected" : "Boost your trust score"}
            </div>
            {v.status === "none" && <div className="text-sm text-muted-foreground mt-1">Verified sellers convert 2× better. Takes 60 seconds.</div>}
            {v.status === "rejected" && v.reason && <div className="text-sm text-destructive mt-1">{v.reason}</div>}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {badge && <span className={`mono text-[10px] px-2 py-1 ${badge.tone}`}>{badge.text}</span>}
          {(v.status === "none" || v.status === "rejected") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="apply-verification-btn" className="mono text-xs bg-foreground text-background px-4 py-2 hover:bg-zinc-800">APPLY NOW</button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle>Apply for verification</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="overline block mb-2">◆ ID type</label>
                    <Input data-testid="ver-id-type" value={idType} onChange={(e) => setIdType(e.target.value)} className="rounded-none border-foreground/20"/>
                  </div>
                  <div>
                    <label className="overline block mb-2">◆ ID number</label>
                    <Input data-testid="ver-id-number" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} className="rounded-none border-foreground/20 mono"/>
                  </div>
                  <div>
                    <label className="overline block mb-2">◆ Anything else? (optional)</label>
                    <Textarea data-testid="ver-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-none border-foreground/20"/>
                  </div>
                  <div>
                    <label className="overline block mb-2">◆ Upload ID document (image)</label>
                    {docPath ? (
                      <div className="border border-foreground/20 p-2 flex items-start gap-3">
                        <img src={fileUrl(docPath)} alt="ID" className="w-24 h-16 object-cover border border-foreground/10"/>
                        <div className="flex-1 min-w-0">
                          <div className="mono text-xs truncate">Document attached</div>
                          <button
                            data-testid="ver-doc-remove"
                            onClick={() => setDocPath("")}
                            className="mono text-xs text-destructive mt-1 inline-flex items-center gap-1"
                          ><X className="w-3 h-3"/> Remove</button>
                        </div>
                      </div>
                    ) : (
                      <label className="inline-flex items-center gap-2 border border-dashed border-foreground/30 px-4 py-3 mono text-xs cursor-pointer hover:border-foreground w-full">
                        <Upload className="w-3.5 h-3.5"/> {uploading ? "UPLOADING…" : "UPLOAD ID IMAGE (JPG/PNG)"}
                        <input
                          data-testid="ver-doc-input"
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleUpload}
                          disabled={uploading}
                        />
                      </label>
                    )}
                    <p className="text-xs text-muted-foreground mono mt-2">Passport or government ID image. Max 8MB.</p>
                  </div>
                </div>
                <DialogFooter>
                  <button data-testid="ver-submit" onClick={submit} disabled={!docPath || uploading} className="bg-primary text-primary-foreground px-6 py-2.5 mono text-xs disabled:opacity-50">SUBMIT</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    </div>
  );
}
